// ============================================================================
// Analytics — replaces the spreadsheet "Template" tab. Views only (no new
// storage): cost per period, payout rate per sub, quarterly compliment rate.
// ============================================================================

import { ErrorState } from '../../shared/components';
import { fmtCurrency } from '../../shared/format';
import { useIncentiveAnalytics } from './hooks/use-incentive-data';
import { formatPeriod, payoutTypeShort } from './format';

export function AnalyticsView() {
  const { cost, rateBySub, complimentRate, isLoading, error, refetch } =
    useIncentiveAnalytics();

  if (error) return <ErrorState body={error} onRetry={refetch} />;
  if (isLoading)
    return <p className="text-sm text-tcs-text-muted">Loading analytics…</p>;

  return (
    <div className="space-y-6">
      <Section title="Performance payment cost per period">
        {cost.length === 0 ? (
          <Empty />
        ) : (
          <Table head={['Period', 'Type', 'Paying', 'Cost (approved)', 'Paid']}>
            {cost.map((r) => (
              <tr key={`${r.payout_type}-${r.period}`} className="border-t border-tcs-border">
                <Td>{formatPeriod(r.period)}</Td>
                <Td muted>{payoutTypeShort(r.payout_type)}</Td>
                <Td>{r.paying_rows}</Td>
                <Td>{fmtCurrency(Number(r.incentive_cost || 0))}</Td>
                <Td>{fmtCurrency(Number(r.paid_cost || 0))}</Td>
              </tr>
            ))}
          </Table>
        )}
      </Section>

      <Section title="Scope Performance Payment rate per sub">
        {rateBySub.length === 0 ? (
          <Empty />
        ) : (
          <Table head={['Sub', 'Period', 'Rows', 'Paid', 'Disq.', 'Payout rate']}>
            {rateBySub.map((r, i) => (
              <tr key={`${r.provider_id ?? 'na'}-${r.period}-${i}`} className="border-t border-tcs-border">
                <Td>{r.provider_name ?? r.provider_id ?? '—'}</Td>
                <Td muted>{formatPeriod(r.period)}</Td>
                <Td>{r.nmi_rows}</Td>
                <Td>{r.paid_rows}</Td>
                <Td>{r.disqualified_rows}</Td>
                <Td>{r.payout_rate_pct != null ? `${r.payout_rate_pct}%` : '—'}</Td>
              </tr>
            ))}
          </Table>
        )}
      </Section>

      <Section title="Quarterly compliment rate">
        {complimentRate.length === 0 ? (
          <Empty />
        ) : (
          <Table head={['Quarter', 'Eligible', 'Earned', 'Paid', 'Cost']}>
            {complimentRate.map((r) => (
              <tr key={r.period} className="border-t border-tcs-border">
                <Td>{formatPeriod(r.period)}</Td>
                <Td>{r.eligible_assignments}</Td>
                <Td>{r.earned_rows}</Td>
                <Td>{r.paid_rows}</Td>
                <Td>{fmtCurrency(Number(r.compliment_cost || 0))}</Td>
              </tr>
            ))}
          </Table>
        )}
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h2 className="mb-2 text-sm font-semibold text-tcs-text">{title}</h2>
      <div className="overflow-x-auto rounded-xl border border-tcs-border bg-tcs-bg">
        {children}
      </div>
    </div>
  );
}

function Table({ head, children }: { head: string[]; children: React.ReactNode }) {
  return (
    <table className="w-full text-left text-sm">
      <thead>
        <tr className="text-[11px] uppercase tracking-wide text-tcs-text-muted">
          {head.map((h) => (
            <th key={h} className="px-3 py-2 font-semibold">
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  );
}

function Td({ children, muted }: { children: React.ReactNode; muted?: boolean }) {
  return (
    <td className={`px-3 py-2 ${muted ? 'text-tcs-text-secondary' : 'text-tcs-text'}`}>
      {children}
    </td>
  );
}

function Empty() {
  return (
    <p className="px-3 py-4 text-sm text-tcs-text-muted">
      No data yet — generate and verify payouts to populate this roll-up.
    </p>
  );
}
